import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Statistic,
  Tag,
  message,
  theme
} from "antd";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import { formatCents, formatDate } from "../shared/money";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";

const KIND_COLOR = {
  invoice: "green",
  beleg: "volcano",
  contract: "geekblue",
  payroll_net: "purple",
  payroll_sv: "purple",
  payroll_lst: "purple",
  scenario: "gold"
};

const LiquidityTab = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenario, setScenario] = useState({ customerDelayDays: 7, extraOutflows: [] });
  const [extra, setExtra] = useState({ week: 0, amount: null, label: "" });

  const load = useCallback(async s => {
    setLoading(true);
    try {
      const res = await api.getLiquidity(s);
      setData(res.data);
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(scenario);
  }, [load, scenario]);

  const addExtra = () => {
    if (!extra.amount) return;
    setScenario(s => ({ ...s, extraOutflows: [...s.extraOutflows, { ...extra }] }));
    setExtra({ week: 0, amount: null, label: "" });
  };

  const weeks = data?.weeks || [];
  const series = [{ name: t("Balance"), data: weeks.map(w => Math.round(w.balance / 100)) }];
  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      animations: { enabled: false },
      fontFamily: "inherit"
    },
    colors: [token.colorPrimary],
    stroke: { width: 2, curve: "straight" },
    fill: { type: "solid", opacity: 0.12 },
    dataLabels: { enabled: false },
    markers: { size: 4, hover: { size: 6 } },
    grid: { borderColor: token.colorBorderSecondary, strokeDashArray: 3 },
    xaxis: {
      categories: weeks.map(w => formatDate(w.start)),
      labels: { rotate: -45, style: { fontSize: "11px", colors: token.colorTextSecondary } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        formatter: v => `${new Intl.NumberFormat("de-DE").format(v)} €`,
        style: { colors: token.colorTextSecondary }
      }
    },
    tooltip: { y: { formatter: v => `${new Intl.NumberFormat("de-DE").format(v)} €` } },
    annotations: {
      yaxis: data
        ? [
            {
              y: Math.round(data.floor / 100),
              borderColor: token.colorError,
              strokeDashArray: 4,
              label: {
                text: `${t("Floor")} ${formatCents(data.floor)}`,
                style: { color: token.colorTextLightSolid, background: token.colorError }
              }
            }
          ]
        : []
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            size="small"
            title={
              <span className="acct-wrap" style={{ whiteSpace: "normal" }}>
                {`${t("Liquidity forecast")} — ${data ? `${formatDate(data.from)} – ${formatDate(data.to)}` : ""}`}
              </span>
            }
            loading={loading && !data}
          >
            {data?.alert ? (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 8 }}
                message={`${t("Balance falls below the floor in week")} ${data.alert.week + 1} (${formatDate(data.alert.start)}): ${formatCents(data.alert.balance)} < ${formatCents(data.alert.floor)}`}
              />
            ) : data ? (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 8 }}
                message={`${t("Lowest balance")}: ${formatCents(data.minimum.balance)} (${t("week")} ${data.minimum.week + 1})`}
              />
            ) : null}
            <Chart type="area" width="100%" height={280} series={series} options={options} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title={t("Scenario")}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Statistic
                title={t("Opening (bank + cash)")}
                value={formatCents(data?.opening?.total)}
              />
              <Form.Item label={t("Customer payment delay (days)")} style={{ marginBottom: 4 }}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={120}
                  value={scenario.customerDelayDays}
                  onChange={v => setScenario(s => ({ ...s, customerDelayDays: v || 0 }))}
                />
              </Form.Item>
              <Form.Item label={t("Extra outflow")} style={{ marginBottom: 4 }}>
                <Row gutter={[8, 8]}>
                  <Col xs={12} lg={24} xl={10}>
                    <InputNumber
                      min={1}
                      max={13}
                      value={extra.week + 1}
                      onChange={v => setExtra(e => ({ ...e, week: (v || 1) - 1 }))}
                      addonBefore={t("Week")}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col xs={12} lg={24} xl={14}>
                    <MoneyInput
                      value={extra.amount}
                      onChange={v => setExtra(e => ({ ...e, amount: v }))}
                    />
                  </Col>
                  <Col xs={24}>
                    <Space.Compact block>
                      <Input
                        placeholder={t("Label")}
                        value={extra.label}
                        onChange={e => setExtra(x => ({ ...x, label: e.target.value }))}
                      />
                      <Button onClick={addExtra}>{t("Add")}</Button>
                    </Space.Compact>
                  </Col>
                </Row>
              </Form.Item>
              {scenario.extraOutflows.map((x, i) => (
                <Tag
                  key={i}
                  closable
                  onClose={() =>
                    setScenario(s => ({
                      ...s,
                      extraOutflows: s.extraOutflows.filter((_, j) => j !== i)
                    }))
                  }
                  color="gold"
                  style={{ whiteSpace: "normal", maxWidth: "100%" }}
                >
                  {t("Week")} {x.week + 1}: <Amount cents={x.amount} /> {x.label}
                </Tag>
              ))}
              <Button onClick={() => setScenario({ customerDelayDays: 7, extraOutflows: [] })}>
                {t("Reset")}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        style={{ marginTop: 16 }}
        size="small"
        pagination={false}
        rowKey="week"
        loading={loading}
        dataSource={weeks}
        expandable={{
          expandedRowRender: w => (
            <Space wrap>
              {w.items.map((i, idx) => (
                <Tag
                  key={idx}
                  color={KIND_COLOR[i.kind] || (i.direction === "in" ? "green" : "red")}
                >
                  {i.direction === "in" ? "+" : "−"}{" "}
                  <span className="acct-num">{formatCents(i.amount)}</span> · {i.label} ·{" "}
                  {formatDate(i.dueDate)}
                  {i.overdue ? ` (${t("overdue")})` : ""}
                </Tag>
              ))}
            </Space>
          )
        }}
        columns={[
          { title: t("Week"), dataIndex: "week", render: w => w + 1, width: 80 },
          { title: t("From"), dataIndex: "start", render: formatDate },
          {
            title: t("Inflows"),
            dataIndex: "in",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Outflows"),
            dataIndex: "out",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Balance"),
            dataIndex: "balance",
            align: "end",
            render: (v, w) => (
              <span
                className="acct-num acct-num--strong"
                style={{ color: data && v < data.floor ? token.colorError : undefined }}
              >
                {formatCents(v)}
                {w.items.length ? ` (${w.items.length})` : ""}
              </span>
            )
          }
        ]}
      />
    </div>
  );
};

export default LiquidityTab;
